import { useNavigate } from 'react-router-dom';
import { setUser } from 'store/auth/authSlice';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';

import FastForwardOutlinedIcon from '@mui/icons-material/FastForwardOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Settings from '@mui/icons-material/Settings';
import LanguageIcon from '@mui/icons-material/Language';
import { MenuButton as BaseMenuButton } from '@mui/base/MenuButton';
import { MenuItem as BaseMenuItem } from '@mui/base/MenuItem';
import { Dropdown } from '@mui/base/Dropdown';
import { Menu } from '@mui/base/Menu';
import styled from '@emotion/styled';
import { Box } from '@mui/material';

import SystemLanguageModal from 'components/Modals/SystemLanguageModal';
import { setIsProductTourFinished } from 'store/global/globalSlice';
import outputSettingsIcon from 'assets/images/icons/output.svg';
import { auth } from 'utils/config/firebase';
import { useAppDispatch } from 'app/hooks';
import { signOut } from 'firebase/auth';

import SignatureModal from 'components/Modals/SignatureModal';
import CountryAndLanguagesModal from 'components/Modals/CountryAndLanguagesModal';

import signatureIcon from 'assets/images/icons/contact_mail.svg';
import { errorLogger } from 'utils/config/firebase/logs';

type SettingsComponentProps = {
    onCloseSidebar: () => void;
};

export const SettingsComponent = ({ onCloseSidebar }: SettingsComponentProps) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const [isOpenLanguageModal, setIsOpenLanguageModal] = useState(false);
    const [isOpenCountryAndLanguagesModal, setIsOpenCountryAndLanguagesModal] = useState(false);
    const [isSignatureOpen, setIsSignatureOpen] = useState(false);

    const onCloseLanguageModal = () => setIsOpenLanguageModal(false);
    const onOpenLanguageModal = () => setIsOpenLanguageModal(true);

    const onCloseSignatureModal = () => setIsSignatureOpen(false);
    const onOpenSignatureModal = () => setIsSignatureOpen(true);

    const onOpenCountryAndLanguagesModal = () => setIsOpenCountryAndLanguagesModal(true);
    const onCloseCountryAndLanguagesModal = () => setIsOpenCountryAndLanguagesModal(false);

    const onOpenProductTour = () => dispatch(setIsProductTourFinished(false));

    const onLogOut = async () => {
        try {
            signOut(auth);
            dispatch(setUser({ isLoggedIn: false }));
            navigate('/sign-in');
        } catch (error) {
            if (error instanceof Error) {
                errorLogger.mediumError(error?.message, 'onLogOut');
            }
            console.log(error);
        }
    };
    return (
        <Box className="settings-container">
            <Dropdown>
                <MenuButton className="settings">
                    <Settings sx={{ fontSize: 24, color: '#000' }} />
                    {t('web_app.home.settings.btn')}
                </MenuButton>
                <Menu
                    slots={{ listbox: Listbox }}
                    style={{ zIndex: 1201, top: 'unset' }}
                    className="dropdown settings-dropdown"
                >
                    <MenuItem>
                        <button
                            className="settings-dropdown-item"
                            onClick={onOpenCountryAndLanguagesModal}
                        >
                            <img src={outputSettingsIcon} alt="brand_icon" />
                            {t('web_app.home.settings.country_and_languages')}
                        </button>
                    </MenuItem>
                    <MenuItem>
                        <button className="settings-dropdown-item" onClick={onOpenLanguageModal}>
                            <LanguageIcon />
                            {t('web_app.home.settings.system_language_title')}
                        </button>
                    </MenuItem>
                    <MenuItem className="has-border">
                        <button className="settings-dropdown-item" onClick={onOpenSignatureModal}>
                            <img src={signatureIcon} alt="brand_icon" />
                            {t('web_app.settings.signature.btn_name')}
                        </button>
                    </MenuItem>
                    <MenuItem>
                        <a
                            className="settings-dropdown-item"
                            href="https://drive.google.com/drive/folders/1X8jVDyRkq0xeEwqBeBFA6q0yH5dJ47lM"
                            target="_blank"
                            rel="noreferrer"
                        >
                            <InfoOutlinedIcon />
                            {t('web_app.home.settings.resources')}
                        </a>
                    </MenuItem>
                    <MenuItem className="has-border">
                        <button className="settings-dropdown-item" onClick={onOpenProductTour}>
                            <FastForwardOutlinedIcon />
                            {t('web_app.home.settings.product_tour')}
                        </button>
                    </MenuItem>
                    <MenuItem>
                        <button className="settings-dropdown-item" onClick={onLogOut}>
                            <LogoutOutlinedIcon />
                            {t('web_app.home.settings.logout')}
                        </button>
                    </MenuItem>
                </Menu>
            </Dropdown>
            <SystemLanguageModal open={isOpenLanguageModal} onClose={onCloseLanguageModal} />
            <CountryAndLanguagesModal
                open={isOpenCountryAndLanguagesModal}
                onClose={onCloseCountryAndLanguagesModal}
                onCloseSidebar={onCloseSidebar}
            />
            <SignatureModal open={isSignatureOpen} onClose={onCloseSignatureModal} />
        </Box>
    );
};

export default SettingsComponent;

const Listbox = styled('ul')(() => ``);
const MenuItem = styled(BaseMenuItem)(() => ``);
const MenuButton = styled(BaseMenuButton)(() => ``);
